// hooks/useExternalApiToggles.ts
import { useCallback, useRef } from "react";
import { conversationService } from "@services/ConversationService";

export function useExternalApiToggles(params: {
  conversationIdRef: React.MutableRefObject<string | null>;
  enabledSources: Set<string>;
  setEnabledSources: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const { conversationIdRef, enabledSources, setEnabledSources } = params;
  const inFlight = useRef<Promise<any> | null>(null);

  const onSourcesChange = useCallback(
    async (next: Set<string>) => {
      const conversationId = conversationIdRef.current
        ? parseInt(conversationIdRef.current, 10)
        : NaN;

      const prev = new Set(enabledSources);
      setEnabledSources(new Set(next));

      if (!conversationId || Number.isNaN(conversationId)) {
        return;
      }

      const added = [...next].filter((k) => !prev.has(k));
      const removed = [...prev].filter((k) => !next.has(k));

      try {
        let latest;

        if (added.length) {
          inFlight.current = conversationService.toggleExternalApis(
            conversationId,
            {
              api_keys: added,
              action: "enable",
            }
          );
          latest = await inFlight.current;
          setEnabledSources(new Set(latest.enabled_api_keys || []));
        }

        if (removed.length) {
          inFlight.current = conversationService.toggleExternalApis(
            conversationId,
            {
              api_keys: removed,
              action: "disable",
            }
          );
          latest = await inFlight.current;
          setEnabledSources(new Set(latest.enabled_api_keys || []));
        }
      } catch (err) {
        setEnabledSources(prev);
        throw err;
      } finally {
        inFlight.current = null;
      }
    },
    [enabledSources, setEnabledSources, conversationIdRef]
  );

  return { onSourcesChange };
}
